<template>
  <div
    class="inline-flex items-center bg-white rounded-full justify-between"
    :class="sessionStore.isClinician ? 'min-w-100' : 'pr-3'"
  >
    <div class="flex items-center">
      <div
        class="content-center rounded-full ml-1 w-14 h-14"
        :class="[
          !sessionStore.isPhasePaused && sessionStore.isClinician
            ? 'bg-vice-dark-orange'
            : 'bg-vice-teal-700',
          { 'pointer-events-none': !sessionStore.isClinician },
        ]"
      >
        <span
          v-if="sessionStore.togglingPausedState"
          class="pointer-events-none h-full inline-flex justify-center ml-3 items-center w-12"
        >
          <ViceLoader />
        </span>

        <button
          data-cy="pause-session"
          class="inline-flex items-center ml-3 w-12 h-full"
          v-show="
            sessionStore.isClinician &&
            !sessionStore.isPhasePaused &&
            !sessionStore.togglingPausedState
          "
          @click="togglePausedState(true)"
          v-tippy="{
            content: 'Pause session',
            theme: 'light-border',
            interactive: true,
          }"
        >
          <PauseIcon
            :class="{ 'opacity-0': sessionStore.togglingPausedState }"
            class="w-6 h-6 text-white ml-1"
          />
        </button>
        <button
          data-cy="continue-session"
          class="inline-flex items-center w-14"
          v-show="
            !sessionStore.isClinician ||
            (sessionStore.isPhasePaused && !sessionStore.togglingPausedState)
          "
          @click="togglePausedState(false)"
          v-tippy="{
            content: 'Continue session',
            theme: 'light-border',
            interactive: true,
          }"
        >
          <PlayIcon
            :class="{ 'opacity-0': sessionStore.togglingPausedState }"
            class="w-14 h-14 white z-30"
          />
        </button>
      </div>

      <div class="flex flex-col">
        <div class="flex ml-0 justify-content-between items-center">
          <span class="mx-2 text-sm text-center font-bold leading-5 text-gray-700">
            {{ currentPhaseData.title }}
          </span>
          <span class="text-sm text-center font-medium leading-5 text-gray-500 mr-2 counter">
            {{ formatedDuration }}
          </span>
          <div
            class="h-8 rounded-full justify-center flex items-center bg-black ml-5"
            :class="[
              sessionStore.isPhasePaused ? 'text-vice-dark-grey w-20' : 'text-vice-light-red w-16',
            ]"
          >
            <PauseIcon v-if="sessionStore.isPhasePaused" class="w-3 h-3 text-vice-orange" />
            <span v-else class="h-2 w-2 text-center mx-0 rounded-full bg-vice-light-red" />

            <span
              class="text-xxs text-center leading-5 font-medium pl-1"
              :class="{ 'text-vice-orange': sessionStore.isPhasePaused }"
            >
              {{ sessionStore.isPhasePaused ? '&nbsp;Paused' : 'Live' }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="flex rounded-r-full relative" v-if="sessionStore.isClinician">
      <Menu as="div" class="relative inline-block text-left my-1">
        <MenuButton
          data-cy="phase-actions"
          class="
            inline-flex
            justify-center
            items-center
            prose-sm
            font-bold
            w-14
            h-14
            mr-1
            border-2 border-gray-200
            text-vice-teal-900
            rounded-full
            shadow-2xl
            transform
            -rotate-90
          "
          id="dropdownBtn"
        >
          <img src="/image/icons/dropdown-icon.svg" alt="Dropdown Icon" />
        </MenuButton>

        <transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="transform opacity-0 scale-95"
          enter-to-class="transform opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="transform opacity-100 scale-100"
          leave-to-class="transform opacity-0 scale-95"
        >
          <MenuItems
            class="
              origin-top-right
              absolute
              right-0
              mt-1
              w-40
              rounded-md
              shadow-lg
              bg-white
              ring-1 ring-black ring-opacity-5
              focus:outline-none
              z-10
            "
          >
            <div class="py-1">
              <MenuItem
                :data-cy="`end-` + currentPhaseData.title.toLowerCase()"
                v-slot="{ active }"
                @click="endCurrentPhase(currentPhaseData)"
                v-if="currentPhaseData.id != VICE_SESSION_PHASES.DEBRIEF"
              >
                <span
                  class="cursor-pointer"
                  :class="[
                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                    'block px-4 py-2 text-sm',
                  ]"
                >
                  End {{ currentPhaseData.title.toLowerCase() }}
                </span>
              </MenuItem>
              <!-- <MenuItem v-slot="{ active }">
                <span
                  data-cy="end-session"
                  @click="handleEndSession(currentPhaseData)"
                  :class="[
                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                    'cursor-pointer block px-4 py-2 text-sm',
                  ]"
                >
                  End session
                </span>
              </MenuItem> -->
              <MenuItem
                data-cy="back-to-previous-phase"
                v-slot="{ active }"
                @click="
                  backToPreviousPhase(currentPhaseData);
                  backToPreviousPhaseFlagTrigger();
                "
                v-if="currentPhaseData.id != VICE_SESSION_PHASES.INTRODUCTION"
              >
                <span
                  class="cursor-pointer"
                  :class="[
                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                    'block px-4 py-2 text-sm',
                  ]"
                >
                  Back to {{ currentPhaseData.previousPhaseTitle.toLowerCase() }}
                </span>
              </MenuItem>
            </div>
          </MenuItems>
        </transition>
      </Menu>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { PlayIcon } from '@heroicons/vue/solid';
import { onMounted, onUnmounted, ref, computed, watch } from 'vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { directive } from 'vue-tippy';
import PauseIcon from '@/Components/Icons/PauseIcon';
import { VICE_SESSION_PHASES } from '@/Utils/vice-constants';
import useSessionStore from '@/Stores/useSessionStore';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light-border.css';

dayjs.extend(duration);

export default {
  props: {
    currentPhaseData: {
      type: Object,
      required: true,
    },
    endCurrentPhase: {
      type: Function,
    },
    backToPreviousPhase: {
      type: Function,
    },
  },
  components: {
    ViceLoader,
    Menu,
    MenuItem,
    PlayIcon,
    MenuItems,
    MenuButton,
    PauseIcon,
  },
  directives: {
    tippy: directive,
  },
  emits: ['endSessionModal'],

  setup(props, { emit }) {
    const timerRef = ref(null);
    const sessionDuration = ref(0);
    const sessionStore = useSessionStore();

    const backToPreviousPhaseFlag = ref(false);

    const backToPreviousPhaseFlagTrigger = () => {
      backToPreviousPhaseFlag.value = true;
    };

    const formatedDuration = computed(() =>
      dayjs.duration(sessionDuration.value).format('H:mm:ss'),
    );

    const startTimer = () => {
      timerRef.value = setInterval(() => {
        sessionDuration.value += 1000;
      }, 1000);
    };

    const stopTimer = () => {
      clearInterval(timerRef.value);
      timerRef.value = null;
    };

    const togglePausedState = (isPaused) => {
      sessionStore.togglePausedState(isPaused);
    };

    watch(
      () => sessionStore.isPhasePaused,
      (isPaused) => {
        if (isPaused) {
          stopTimer();
        } else {
          startTimer();
        }
      },
    );

    const showEndSessionModal = () => {
      emit('endSessionModal');
    };

    const handleEndSession = (currentPhaseData) => {
      if (currentPhaseData.id == VICE_SESSION_PHASES.DEBRIEF) {
        props.endCurrentPhase(currentPhaseData);
      } else {
        showEndSessionModal();
      }
    };

    // TODO: access time calculation directly from the store!
    const addPassedTimeFromStore = () => {
      sessionDuration.value = sessionStore.currentPhaseData.duration;
      if (!sessionStore.isPhasePaused) {
        const diffInSeconds = dayjs().diff(
          dayjs(sessionStore.currentPhaseData.lastTimeStamp),
          'seconds',
        );

        sessionDuration.value += diffInSeconds * 1000;
      }
    };

    onMounted(() => {
      addPassedTimeFromStore();
      if (!sessionStore.isPhasePaused) startTimer();
    });

    // When it unmounts, it means phase has ended. Clear the timer.
    onUnmounted(() => {
      // Update the store directly from the startTimer to avoid unneccesary step
      // Update the final duration  for the phase
      sessionStore.updatePhaseDuration(
        props.currentPhaseData.id,
        sessionDuration.value,
        backToPreviousPhaseFlag.value,
      );
      stopTimer();
    });

    return {
      sessionStore,
      formatedDuration,
      VICE_SESSION_PHASES,
      handleEndSession,
      togglePausedState,
      showEndSessionModal,
      backToPreviousPhaseFlagTrigger,
    };
  },
};
</script>

<style lang="scss" scoped>
.pause {
  box-shadow: -1px 0px 4px rgba(0, 0, 0, 0.29) !important;
  border-radius: 0px 76px 76px 0px !important;
}

.shadow-inner-blur {
  box-shadow: inset 0px 4px 4px 0 rgba(0, 0, 0, 0.25);
}
.counter {
  min-width: 64px;
}
</style>
